import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useEffect, useState } from "react";
import SelectBox from "@/components/ui/SelectBox";
import { useCodeListByPriceCode, useCodeList } from "@/hooks/standard/st_code/useCode";
import {
  usePriceCode,
  usePriceExtraCode,
  usePriceCodeDelete,
  usePriceExtraCodeDelete,
  useAdjustPrice,
  usePricePercentage,
  useSavePercentage,
} from "@/hooks/standard/st_price_code/usePriceCode";
import { useUserInfo } from "@/hooks/member/useMember";
import { formatNumber } from "@/lib/utils";
import AddModal from "@/components/modal/standard/priceCode/Add";
import { toast } from "sonner";
import UpdateModal from "@/components/modal/standard/priceCode/Update";

export default function UnitPriceCodePage() {
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState(""); // 조회 버튼 클릭 시 적용되는 검색어
  const { data: priceCodeList } = useCodeListByPriceCode();
  const { data: userInfo } = useUserInfo();
  const [selectedDeparture, setSelectedDeparture] = useState<string | undefined>(undefined);
  const [selectedArrival, setSelectedArrival] = useState<number | undefined>(undefined); // 도착지 선택 상태 추가
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [editData, setEditData] = useState<
    | {
        unitCodeData?: any;
        extraCodeData?: any;
      }
    | undefined
  >(undefined);

  // 각 테이블별 선택 상태
  const [selectedRows1, setSelectedRows1] = useState<number[]>([]);
  const [selectedRows2, setSelectedRows2] = useState<number[]>([]);
  const [selectedRows3, setSelectedRows3] = useState<number[]>([]);
  const [tableVer, setTableVer] = useState(0);
  const [selectedRow1, setSelectedRow1] = useState<any>(null);

  const deletePriceCode = usePriceCodeDelete();
  const deletePriceExtraCode = usePriceExtraCodeDelete();
  const adjustPrice = useAdjustPrice();
  const savePercentage = useSavePercentage();

  // 퍼센테이지 조정 상태
  const [percentage, setPercentage] = useState<string>("");

  // 회사별 저장된 퍼센테이지 조회
  const { data: savedPercentage } = usePricePercentage(selectedRow1?.codeId, {
    enabled: !!selectedRow1?.codeId,
  });

  // 회사 선택 시 저장된 퍼센테이지 불러오기
  useEffect(() => {
    if (savedPercentage !== undefined && savedPercentage !== null) {
      setPercentage(savedPercentage.toString());
    } else {
      setPercentage("");
    }
  }, [savedPercentage, selectedRow1?.codeId]);
  // 선택된 출발지가 있을 때만 도착지 정보 조회
  const { data: priceCodeList2, isError } = usePriceCode(
    {
      stPriceUnitCode: selectedArrival?.toString(),
      search: searchValue,
      page: 0,
      size: 20,
      sort: "stDist,asc",
    },
    {
      enabled: !!selectedArrival,
    }
  );

  const { data: priceExtraCodeList } = usePriceExtraCode(
    {
      stPriceExtraCode: selectedRow1?.codeId,
      search: searchValue,
      page: 0,
      size: 20,
    },
    {
      enabled: !!selectedRow1?.codeId,
    }
  );

  // 첫 번째 테이블: 회사 목록
  const columns1: TableColumn[] = [{ key: "name", header: "회사 목록", bodyAlign: "left" }];

  // 두 번째 테이블: 도착지역 옵션 (동적 컬럼 생성)
  // content의 첫 번째 항목에서 동적 컬럼명 추출
  const formatWeightLabel = (value: string) => {
    if (value === "stPriceUnitCode") {
      return;
    }
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return Number.isInteger(numeric) ? numeric.toString() : numeric.toString();
    }
    return value;
  };

  const firstItem = priceCodeList2?.content?.[0];
  const dynamicColumns: TableColumn[] = firstItem
    ? Object.keys(firstItem)
        .filter(
          key => key !== "stName" && key !== "stDist" && key !== "name" && key !== "dist" && key !== "stPriceUnitCode"
        )
        .sort((a, b) => {
          // 숫자로 변환 가능한 경우 숫자 순서로 정렬
          const numA = parseFloat(a);
          const numB = parseFloat(b);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          // 숫자가 아닌 경우 알파벳 순서로 정렬
          return a.localeCompare(b);
        })
        .map(key => ({
          key: key,
          header: `~ ${formatWeightLabel(key)}t`,
          bodyAlign: "right" as const,
          render: (value: any) => (value != null ? formatNumber(value) : ""),
        }))
    : [];

  const columns2: TableColumn[] = [
    { key: "stName", header: "도착지역", bodyAlign: "left" },
    {
      key: "stDist",
      header: "거리",
      bodyAlign: "center",
      render: (value: any) => {
        const numeric = Number(value);
        if (value == null || (!Number.isNaN(numeric) && numeric === 0)) {
          return "";
        }
        return value;
      },
    },
    ...dynamicColumns,
  ];

  // 세 번째 테이블: 추가 비용
  const columns3: TableColumn[] = [
    { key: "stName", header: "추가", bodyAlign: "left" },
    {
      key: "stExtraPrice",
      header: "비용",
      bodyAlign: "right",
      render: (value: any) => (value != null ? formatNumber(value) : ""),
    },
    { key: "memo", header: "비고", bodyAlign: "left" },
  ];

  const { data: arrilvalList } = useCodeList(
    {
      parentId: selectedRow1?.codeId,
    },
    {
      enabled: !!selectedRow1?.codeId,
    }
  );

  // 도착지 목록이 로드되면 첫 번째 항목을 자동 선택
  useEffect(() => {
    if (arrilvalList && arrilvalList.length > 0 && !selectedArrival) {
      setSelectedArrival(arrilvalList[0].codeId);
    }
  }, [arrilvalList, selectedArrival]);

  // 회사 목록 필터링 (조회 버튼 클릭 시 적용된 검색어로 필터)
  const filteredPriceCodeList = (priceCodeList ?? []).filter((item: any) =>
    appliedSearchValue ? item.name?.toLowerCase().includes(appliedSearchValue.toLowerCase()) : true
  );

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    setAppliedSearchValue(searchValue);
  };

  // Enter 키 핸들러
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 테이블 데이터
  const data1: any[] = filteredPriceCodeList;
  const data2: any[] = priceCodeList2?.content ?? [];
  const data3: any[] = priceExtraCodeList?.content ?? [];

  // 첫 번째 테이블 선택 핸들러
  const handle1RowClick = (row: any) => {
    setSelectedRow1(row);
    setSelectedArrival(undefined); // 출발지 변경 시 도착지 선택 초기화
  };

  const handleSave = () => {
    setTableVer(v => v + 1);
    setSelectedRows2([]);
    setSelectedRows3([]);
    setIsAddModalOpen(false);
  };

  const handleDelete = async () => {
    if (selectedRows2.length === 0 && selectedRows3.length === 0) {
      toast.warning("삭제할 데이터를 선택해주세요.");
      return;
    }

    const unitCodeList = selectedRows2
      .map((rowIndex: number) => ({
        stPriceUnitCode: data2[rowIndex]?.stPriceUnitCode,
        stDist: data2[rowIndex]?.stDist ?? 0, // null/undefined면 0으로 치환
      }))
      .filter((code): code is { stPriceUnitCode: number; stDist: string } => code !== undefined && code !== null);

    const extraCodeList = selectedRows3
      .map((rowIndex: number) => ({
        stPriceExtraCode: data3[rowIndex]?.stPriceExtraCode,
        stParentCode: selectedRow1?.codeId,
      }))
      .filter(
        (code): code is { stPriceExtraCode: number; stParentCode: number } => code !== undefined && code !== null
      );

    if (unitCodeList.length === 0 && extraCodeList.length === 0) {
      toast.warning("유효한 삭제 대상이 없습니다.");
      return;
    }

    // 필요하면 확인창
    if (!window.confirm(`선택한 ${selectedRows2.length + selectedRows3.length}개의 단가비를 삭제하시겠습니까?`)) {
      return;
    }

    // 여러 개를 동시에 삭제
    await Promise.all([
      ...unitCodeList.map(
        unitCode => deletePriceCode.mutateAsync(unitCode) // 문자열이면 Number 변환
      ),
      ...extraCodeList.map(extraCode => deletePriceExtraCode.mutateAsync(extraCode)),
    ]);

    // 삭제 후 선택 상태 초기화
    setSelectedRows2([]);
    setSelectedRows3([]);
    setTableVer(v => v + 1);
  };

  const handleUpdate = () => {
    if (selectedRows2.length + selectedRows3.length === 1) {
      // 두 번째 테이블(단가비)에서 선택된 행이 있는지 확인
      if (selectedRows2.length === 1) {
        const selectedRow = data2[selectedRows2[0]];
        console.log(selectedRow);
        setEditData({
          unitCodeData: selectedRow,
          extraCodeData: undefined,
        });
        setIsUpdateModalOpen(true);
        return;
      }

      // 세 번째 테이블(추가비용)에서 선택된 행이 있는지 확인
      if (selectedRows3.length === 1) {
        const selectedRow = data3[selectedRows3[0]];
        setEditData({
          unitCodeData: undefined,
          extraCodeData: selectedRow,
        });
        setIsUpdateModalOpen(true);
        return;
      }
    }
    toast.warning("수정할 데이터를 1개 선택해주세요.");
  };

  const handleUpdateSave = () => {
    setTableVer(v => v + 1);
    setSelectedRows2([]);
    setSelectedRows3([]);
    setIsUpdateModalOpen(false);
    setEditData(undefined);
  };

  const handle2RowSelect = (rows: number[]) => {
    setSelectedRows2(rows);
    setSelectedDeparture(data2[rows[0]]?.stPriceUnitCode);
  };

  // 단가 일괄 조정 핸들러
  const handleAdjustPrice = () => {
    if (!selectedRow1?.codeId) {
      toast.warning("회사를 선택해주세요.");
      return;
    }
    const percentValue = parseFloat(percentage);
    if (isNaN(percentValue) || percentValue === 0) {
      toast.warning("조정할 퍼센테이지를 입력해주세요.");
      return;
    }
    if (
      !window.confirm(
        `${selectedRow1.name}의 모든 단가를 ${percentValue > 0 ? "+" : ""}${percentValue}% 조정하시겠습니까?`
      )
    ) {
      return;
    }
    adjustPrice.mutate(
      {
        companyCodeId: selectedRow1.codeId,
        percentage: percentValue,
      },
      {
        onSuccess: () => {
          setPercentage("");
          setTableVer(v => v + 1);
        },
      }
    );
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <SearchBoxLayout>
          <div className="flex items-end mb-3">
            <InputBox
              label="회사명"
              className="w-50"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="회사명 검색"
            />

            <div className="flex gap-[6px] ml-auto">
              <Button
                variant="reset"
                onClick={() => {
                  setSearchValue("");
                  setAppliedSearchValue("");
                }}
              >
                초기화
              </Button>
              <Button variant="search" onClick={handleSearch}>
                조회
              </Button>
            </div>
          </div>
        </SearchBoxLayout>
        <main className="flex-1 px-4 flex flex-col min-h-0">
          <div className="flex items-center justify-end mb-2">
            <div className="flex gap-2">
              <PermissionButton
                permission="canCreate"
                variant="etc"
                onClick={() => {
                  if (!selectedRow1) {
                    toast.warning("회사를 선택해주세요.");
                    return;
                  }
                  setSelectedRows2([]);
                  setSelectedRows3([]);
                  setIsAddModalOpen(true);
                }}
              >
                추가
              </PermissionButton>
              <PermissionButton permission="canUpdate" variant="etc" onClick={handleUpdate}>
                수정
              </PermissionButton>
              <PermissionButton permission="canDelete" variant="etc" onClick={handleDelete}>
                삭제
              </PermissionButton>
            </div>
          </div>

          {/* 3개의 테이블: 왼쪽 1개, 오른쪽 2개 세로 배치 */}
          <div className="flex-1 min-h-0 flex gap-4">
            {/* 왼쪽: 첫 번째 테이블 - 회사 목록 */}
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex items-center mb-2 h-[40px]">
                <CountBox count={filteredPriceCodeList.length} />
              </div>
              <Table
                showRowNumber
                resizable
                highlightOnClick
                className="h-full"
                columns={columns1}
                data={data1}
                selectedRows={selectedRows1}
                onRowSelect={setSelectedRows1}
                onRowClick={handle1RowClick}
                maxHeight="100%"
              />
            </div>

            {/* 오른쪽: 두 번째와 세 번째 테이블 세로 배치 */}
            <div className="flex flex-col flex-4 min-h-0 gap-4">
              {/* 두 번째 테이블: 도착지역 옵션 */}
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center gap-2 mb-2 h-[40px]">
                  <SelectBox
                    className="w-75"
                    value={selectedArrival?.toString() || ""}
                    onChange={value => setSelectedArrival(Number(value))}
                    options={
                      arrilvalList?.map((item: any) => ({
                        label: item.name,
                        value: item.codeId.toString(),
                      })) ?? []
                    }
                  />
                  <InputBox
                    className="w-20 text-right"
                    type="number"
                    value={percentage}
                    onChange={e => setPercentage(e.target.value)}
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-600">%</span>
                  <PermissionButton
                    permission="canUpdate"
                    variant="etc"
                    onClick={() => {
                      if (!selectedRow1?.codeId) {
                        toast.warning("회사를 선택해주세요.");
                        return;
                      }
                      const percentValue = parseFloat(percentage);
                      if (isNaN(percentValue)) {
                        toast.warning("퍼센테이지를 입력해주세요.");
                        return;
                      }
                      savePercentage.mutate({
                        companyCodeId: selectedRow1.codeId,
                        percentage: percentValue,
                        modifiedBy: userInfo?.name || "",
                      });
                    }}
                    disabled={savePercentage.isPending}
                  >
                    {savePercentage.isPending ? "저장중..." : "저장"}
                  </PermissionButton>
                </div>
                <Table
                  key={`price-code-table-v${tableVer}`}
                  showRowNumber
                  showCheckbox
                  resizable
                  highlightOnClick
                  className="h-full"
                  columns={columns2}
                  data={data2}
                  selectedRows={selectedRows2}
                  onRowSelect={handle2RowSelect}
                  maxHeight="100%"
                  emptyMessage={data2.length === 0 ? "조회된 데이터가 없습니다." : undefined}
                />
              </div>

              {/* 세 번째 테이블: 추가 비용 */}
              <div className="flex flex-col flex-1 min-h-0">
                <Table
                  key={`price-extra-code-table-v${tableVer}`}
                  showCheckbox
                  showRowNumber
                  resizable
                  highlightOnClick
                  className="h-full"
                  columns={columns3}
                  data={data3}
                  selectedRows={selectedRows3}
                  onRowSelect={setSelectedRows3}
                  maxHeight="100%"
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      <AddModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        companyCode={selectedRow1?.codeId}
        arrivalCodeList={arrilvalList || []}
        onSave={handleSave}
      />
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setEditData(undefined);
        }}
        arrivalCodeList={arrilvalList || []}
        companyCode={selectedRow1?.codeId}
        arrivalCode={selectedArrival?.toString() || ""}
        departureCode={selectedDeparture?.toString() || ""}
        onSave={handleUpdateSave}
        editData={editData}
      />
    </>
  );
}
